import {
  type BatchRemoveLabelFromInboxConversationsMutation,
  type FetchResult,
  InboxConversationsDocument,
  type InboxConversationsQuery,
  type MutationResult,
  useBatchRemoveLabelFromInboxConversationsMutation,
} from "@qw/graphql";
import type { KnownInboxLabel } from "@qw/qw-common";
import { usePagination, useToast } from "@qw/ui";
import { useCallback } from "react";

import type { MaybePartialInboxConversation } from "../types";
import { useInboxFilter } from "./inbox-filter-provider";

interface UseBatchRemoveLabelFromConversationsProps {
  conversations: MaybePartialInboxConversation[];
  label: string;
  successMessage?: string;
  errorMessage?: string;
}

type UseBatchRemoveLabelFromConversationsReturn = [
  (
    props: UseBatchRemoveLabelFromConversationsProps
  ) => Promise<FetchResult<BatchRemoveLabelFromInboxConversationsMutation>>,
  MutationResult<BatchRemoveLabelFromInboxConversationsMutation>,
];

export const useBatchRemoveLabelFromConversations = (): UseBatchRemoveLabelFromConversationsReturn => {
  const { numRecords, setNumRecords, rowsPerPage } = usePagination();
  const { toast } = useToast();
  const { filters: inboxFilter } = useInboxFilter();
  const [removeLabelMutation, result] = useBatchRemoveLabelFromInboxConversationsMutation({
    refetchQueries: ["InboxConversations", "InboxLabelUnreadCounts"],
  });

  const batchRemoveLabel = useCallback(
    async ({ conversations, label, successMessage, errorMessage }: UseBatchRemoveLabelFromConversationsProps) => {
      const canOptimisticallyUpdateRowMetadata = conversations.every(
        (conversation) =>
          "attachmentPreviews" in conversation && "draftCount" in conversation && "messageCount" in conversation
      );

      return await removeLabelMutation({
        variables: { input: { conversationIds: conversations.map((conversation) => conversation.id), label } },
        update(cache, { data }) {
          const conversations = data?.batchRemoveLabelFromInboxConversations;

          if (!conversations) {
            return;
          }

          // Read the existing data from the cache
          const existingData: InboxConversationsQuery | null = cache.readQuery({
            query: InboxConversationsDocument,
            // NOTE: We need to pass the filters used by the current conversations query to access the cache.
            variables: { input: inboxFilter },
          });

          const existingConversations = existingData?.inboxConversations.conversations;

          // If the label is in the filters, we need to update the cache
          if (existingConversations && inboxFilter.labels?.includes(label as KnownInboxLabel)) {
            // Filter out the conversations that were removed from the list.
            const updatedConversations = existingConversations.filter(
              (conversation) => !conversations.some((c) => c.id === conversation.id)
            );

            const updatedNumRecords = numRecords - conversations.length;
            const updatedNumPages = Math.ceil(updatedNumRecords / Number(rowsPerPage));

            cache.writeQuery({
              query: InboxConversationsDocument,
              variables: { input: inboxFilter },
              data: {
                inboxConversations: {
                  ...existingData.inboxConversations,
                  conversations: updatedConversations,
                  numRecords: updatedNumRecords,
                  numPages: updatedNumPages,
                },
              },
            });

            setTimeout(() => setNumRecords(updatedNumRecords), 50);
          }
        },
        ...(canOptimisticallyUpdateRowMetadata
          ? {
              optimisticResponse: {
                batchRemoveLabelFromInboxConversations: conversations.map((conversation) => ({
                  ...conversation,
                  attachmentPreviews: "attachmentPreviews" in conversation ? conversation.attachmentPreviews : [],
                  draftCount: "draftCount" in conversation ? conversation.draftCount : 0,
                  messageCount: "messageCount" in conversation ? conversation.messageCount : 0,
                  labels: (conversation.labels ?? []).filter((l) => l !== label),
                })),
              },
            }
          : {}),
        onCompleted: () => {
          if (successMessage) {
            toast({
              variant: "default",
              title: successMessage,
            });
          }
        },
        onError: () => {
          if (errorMessage) {
            toast({
              variant: "destructive",
              title: errorMessage,
            });
          }
        },
      });
    },
    [removeLabelMutation, inboxFilter, numRecords, setNumRecords, rowsPerPage, toast]
  );

  return [batchRemoveLabel, result];
};
